import { Component, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { OrganizationService } from '../../../../core/services/organization.service';
import { CommonModule } from '@angular/common';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { FormsModule } from '@angular/forms';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { LocalStorageKeyConstants, PaginationConstants } from '../../../../core/constants/constants';
import { BillingStatuses, OrganizationStatuses, PricingTiers, SelectionPrimaryKeys } from '../../../../core/enums/data.enums';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { OrganizationListSortOptions } from '../../../../core/enums/sort-options.enums';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { ItemSelectionPipe } from "../../../../core/pipes/item-selection/item-selection-pipe";
import { OrganizationSearchFilterComponent } from "../organization-search-filter/organization-search-filter.component";
import { AddOrganizationComponent } from '../add-organization/add-organization.component';
import { FormatLabelPipe } from '../../../../core/pipes/format-label/format-label.pipe';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { MemberFormModalComponent } from "../../../member/components/member-form-modal/member-form-modal.component";
import { MemberForm } from '../../../../forms/member/member.form/member.form';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../../core/directives/disable-without-permission/disable-without-permission.directive';
import { MemberPermission, OrganizationPermission } from '../../../../core/enums/permissions.enums';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-organization-list',
  imports: [
    PageTitleComponent,
    FontAwesomeModule,
    PaginationComponent,
    FormsModule,
    CommonModule,
    ConfirmationModalComponent,
    InfoModalComponent,
    InViewPortDirective,
    SortDropdownComponent,
    ItemSelectionPipe,
    OrganizationSearchFilterComponent,
    MobileCardCheckbox,
    AddOrganizationComponent,
    MemberFormModalComponent,
    RouterLink,
    IfHasPermissionDirective,
    DisableWithoutPermissionDirective,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    FormatLabelPipe,
    StatusBadgeComponent,
    TooltipDirective
],
  templateUrl: './organization-list.component.html',
  host: {"style":"display:contents"}
})
export class OrganizationListPage extends BaseListPageClass implements OnInit {   
  @ViewChild(MemberForm) memberFormRef!: MemberForm;

  //Component Data
  public organizationList = signal<OrganizationModel[]>([]);
  public targetedOrganization: OrganizationModel | null = null;

  public OrganizationStatuses = OrganizationStatuses;
  public OrganizationPermission = OrganizationPermission;
  public MemberPermission = MemberPermission;
  public SelectionPrimaryKeys = SelectionPrimaryKeys;
  public OrganizationsMessagingConstants = OrganizationsMessagingConstants;
  
  // Sort
  public organizationListSortOptions = OrganizationListSortOptions;

  public skeletonOrganizations: OrganizationModel[] = [];

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  public confirmationCancelFunction: Function = () => {};
  public confirmationConfirmFunction: Function = () => {};
  
  public isSubmitting = false;

  @ViewChild('organizationListConfirmationModalRef') organizationListConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('organizationListInfoModalRef') organizationListInfoModalRef!: InfoModalComponent;
  @ViewChild(AddOrganizationComponent) addOrganizationRef!: AddOrganizationComponent;
  @ViewChild(MemberFormModalComponent) memberFormModalRef!: MemberFormModalComponent;

  constructor(
    private organizationService: OrganizationService,
    private bootstrapService: BootstrapService,
    public userSessionService: UserSessionService,
    private router: Router,
    private activatedRoute: ActivatedRoute) {
      super();
  }

  public ngOnInit(): void {
    this.createSkeletonOrganizations();
    this.orderBy = this.userSessionService.organizationsOrderBy;
    this.sortOptions = [...ListConstants.ORGANIZATIONS_SORT_OPTIONS()];
    //Instead of loadOrganizations being called here we are calling it from the filter control
    // then firing it's filterApplied event since we are wanting default filters on the initial page load

    this.openAddModalFromQueryParams(this.activatedRoute, '#addOrganizationModal', () => this.addOrganizationRef.openAddOrganizationModal());
  }

  public onAddOrganizationClicked(): void {
    this.addOrganizationRef.openAddOrganizationModal();
  }

  private createSkeletonOrganizations(): void {
    this.skeletonOrganizations = Array.from({ length: this.take }, (_, index) => ({
      organizationId: index,
      accountId: index,
      name: 'Loading...',
      billingStatus: BillingStatuses.Trial,
      pricingTier: PricingTiers.Standard,
      status: OrganizationStatuses.None
    } as OrganizationModel));
  }

  public get displayOrganizations(): OrganizationModel[] {
    const organizationListValue = this.organizationList();
    if (this.isLoading && (!organizationListValue || organizationListValue.length === 0)) {
      return this.skeletonOrganizations;
    }
    return organizationListValue || [];
  }

  public clearSearch(): void {
    this.userSessionService.organizationsFilter.searchTerm = "";
  }

  public async loadOrganizations(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    const response: ServiceResultModel<PaginatedListModel<OrganizationModel>> = await this.organizationService.searchOrganizations(this.userSessionService.organizationsFilter, this.orderBy, this.skip(), this.take);
        
    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append == true) {        
          this.organizationList.set(this.organizationList().concat(response.data?.data));
        } else {      
          this.organizationList.set(response.data?.data);
          //Clear Selected Items
          this.isSelectAll = false;
          this.selectionItems.set([]);
      }
    }

    this.isLoading = false;
    this.calculateSelected();
  }

  public async loadMore(): Promise<void> {
    this.loadOrganizations(true);
  }
  
  public jumpToTop(element: string) : void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  public filterApplied(): void {
    this.loadOrganizations();
  }

  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [OrganizationListSortOptions, OrganizationListSortOptions]> = {
      "Organization Name": [OrganizationListSortOptions.OrganizationNameASC, OrganizationListSortOptions.OrganizationNameDESC],
      "Billing Status": [OrganizationListSortOptions.BillingStatusASC, OrganizationListSortOptions.BillingStatusDESC],
      "Organization Status": [OrganizationListSortOptions.OrganizationStatusASC, OrganizationListSortOptions.OrganizationStatusDESC],
      "Pricing Tier": [OrganizationListSortOptions.PricingTierASC, OrganizationListSortOptions.PricingTierDESC],
      "Members": [OrganizationListSortOptions.MemberCountASC, OrganizationListSortOptions.MemberCountDESC]
    };

    const [ascOption, descOption] = columnSortMap[columnName]
    // fall back to default if columnName isn't present
     || [OrganizationListSortOptions.OrganizationNameASC, OrganizationListSortOptions.OrganizationNameDESC];

    let sortDirection: OrganizationListSortOptions;

    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.setSortOrder(sortDirection);
  }

  public setSortOrder(organizationListSortOption: string) {
    this.orderBy = organizationListSortOption;
    this.userSessionService.organizationsOrderBy = this.orderBy;
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.ORGANIZATIONS_ORDERBY(), this.orderBy);
    this.filterApplied();
  }
  
  //#region table scrolling
  public loadMoreInView(): void {       
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
  //endregion
  
  public openActivationConfirmationModal(organization: OrganizationModel | null): void {
    this.targetedOrganization = organization;

    const isDeactivating = organization?.status === OrganizationStatuses.Active;
    const confirmationMessage = (isDeactivating
      ? OrganizationsMessagingConstants.ORGANIZATIONS_DEACTIVATE_CONFIRMATION()
      : OrganizationsMessagingConstants.ORGANIZATIONS_REACTIVATE_CONFIRMATION())
      .replaceValuesWithObject({ organizationName: organization?.name });

    this.organizationListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.organizationListConfirmationModalRef.message.set(confirmationMessage);

    this.organizationListConfirmationModalRef.enableNotes.set(false);
    this.organizationListConfirmationModalRef.isNoteRequired.set(false);

    this.organizationListConfirmationModalRef.enableTypeToConfirm.set(true);
    this.organizationListConfirmationModalRef.typeToConfirmText.set(isDeactivating
      ? OrganizationsMessagingConstants.ORGANIZATIONS_DEACTIVATE_TYPE_TO_CONFIRM()
      : OrganizationsMessagingConstants.ORGANIZATIONS_REACTIVATE_TYPE_TO_CONFIRM());
    this.organizationListConfirmationModalRef.confirmButtonText.set(isDeactivating
      ? OrganizationsMessagingConstants.ORGANIZATIONS_DEACTIVATE_ACTION_LABEL()
      : OrganizationsMessagingConstants.ORGANIZATIONS_REACTIVATE_ACTION_LABEL());
    this.organizationListConfirmationModalRef.confirmButtonClassName.set(isDeactivating ? 'btn-danger' : 'btn-primary');

    this.setActivationConfirmationModalFunctions();

    this.bootstrapService.manageModal("#organizationListConfirmationModal", ModalActions.Show);
  }

  public setActivationConfirmationModalFunctions() : void {
    this.confirmationConfirmFunction = this.confirmedOrganizationStatusConfirmation;
    this.confirmationCancelFunction = this.onCancelActivation;
  }

  public async confirmedOrganizationStatusConfirmation(_note: string | undefined): Promise<void> {
    this.isSubmitting = true;
    try {
      let activate: boolean = false;
      
      if (this.targetedOrganization?.status === OrganizationStatuses.Active) {
        activate = false;
      } else if (this.targetedOrganization?.status === OrganizationStatuses.Inactive) {
        activate = true;
      }
      
      const response = await this.organizationService.setActivation(this.targetedOrganization?.organizationId!, activate);

      if (response.success === false) {
        //Error response received
        this.organizationListInfoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        //No error response
        this.organizationListInfoModalRef.modalType.set(ModalTypes.Success);
        
        this.selectionItems.set([]);
        this.loadOrganizations();
      }
      
      this.organizationListInfoModalRef.message.set(response.message);
      
      this.bootstrapService.manageModal("#organizationListInfoModal", ModalActions.Show);
      this.bootstrapService.manageModal("#organizationListConfirmationModal", ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }

  public onCancelActivation(): void {
    this.bootstrapService.manageModal("#organizationListConfirmationModal", ModalActions.Hide);
  }

  public setActiveOrganization(organization: OrganizationModel): void {    
    this.userSessionService.activeOrganization.set(organization);
    this.router.navigate(["/members"]);
  }

  public openAddMemberModal(organization: OrganizationModel): void {
    this.targetedOrganization = organization;
    this.memberFormModalRef.openMemberModal();
  }
}
